import { Stack } from '@mui/material'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { ImageSkeleton } from '../ImageSkeleton'
import { useGetAssetById } from '../../api/contentful/contentfulQueries'
import { parseContenfulImageUrl } from '../../contentful/utils'
import { QuestionDescription } from '@estercare/ester-shared'
import { BodyLarge } from '../styles/Typography'

interface Props {
  src: string
  alt: string
  description?: QuestionDescription
}

export const ScreeningInfoSection = ({ alt, src, description }: Props) => {
  const { data, isLoading } = useGetAssetById(src)
  const imageUrl = data ? parseContenfulImageUrl(data, { width: 800, height: 600 }) : undefined

  const hasDescription = !!description?.text

  return (
    <Container $hasDescription={hasDescription}>
      <ImageWrapper $centered={!hasDescription}>
        {isLoading || !data ? <ImageSkeleton /> : <Img src={imageUrl} alt={alt} />}
      </ImageWrapper>
      {hasDescription && <InfoText>{description.text}</InfoText>}
    </Container>
  )
}

const Container = styled(Stack)<{ $hasDescription: boolean }>`
  && {
    align-items: center;
    gap: ${theme.spacing.medium}px;
    color: ${theme.color.grey5};
    flex-direction: column;

    @media screen and (min-width: ${theme.breakpoint.medium}px) {
      flex-direction: ${({ $hasDescription }) => ($hasDescription ? 'row' : 'column')};
      align-items: ${({ $hasDescription }) => ($hasDescription ? 'flex-start' : 'center')};
      gap: ${({ $hasDescription }) => ($hasDescription ? `${theme.spacing.large}px` : `${theme.spacing.medium}px`)};
    }
  }
`

const ImageWrapper = styled.div<{ $centered: boolean }>`
  flex-shrink: 0;
  display: flex;
  justify-content: center;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    width: ${({ $centered }) => ($centered ? 'auto' : '45%')};
    max-width: ${({ $centered }) => ($centered ? '400px' : '280px')};
  }

  @media screen and (max-width: ${theme.breakpoint.medium - 1}px) {
    width: 100%;
  }
`

const InfoText = styled(BodyLarge)`
  color: ${theme.color.black};
  text-align: left;
  flex: 1;
  min-width: 0;
`

const Img = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: 8px;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    max-height: 280px;
  }

  @media screen and (max-width: ${theme.breakpoint.medium - 1}px) {
    max-width: 300px;
    max-height: 200px;
  }
`
